import {
  applySnapshot,
  flow,
  getSnapshot,
  SnapshotOrInstance,
  types,
} from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { reorder } from "utils/calculation";
import { getNextPublishStatus } from "utils/publish";

import {
  ActiveProgram,
  PROGRAM_PERMISSION_TYPES,
  PUBLISH_STATUSES,
} from "constants/models";
import { REORDER_MOVE_TYPES } from "constants/reorder";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";

export const ResourceCategoryModel = types.model({
  id: types.number,
  title: types.string,
  note: types.maybe(types.string),
  publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
});

export const CategoryDetailsModel = types
  .model({
    id: types.number,
    title: types.string,
    note: types.maybe(types.string),
    activePrograms: types.array(ActiveProgram),
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
        activePrograms: self.activePrograms.map(({ id, permission }) => ({
          id,
          switchState: permission === PROGRAM_PERMISSION_TYPES.PREVIEW,
        })),
      };
    },
  }));

const InAppResourcesStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/quick-resource/category/list`, params);
  }),
  itemModel: ResourceCategoryModel,
})
  .props({
    categoryDetails: types.maybe(CategoryDetailsModel),
    isCategoryLoading: types.optional(types.boolean, false),
    isCategoryManaging: types.optional(types.boolean, false),
    isCategoryReordering: types.optional(types.boolean, false),
    isCategoryPublishing: types.optional(types.boolean, false),
    isAllContentPublishing: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
    isCategoryCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },
  }))
  .actions(self => ({
    getReorderInfo(
      reorderedItems: SnapshotOrInstance<typeof ResourceCategoryModel>[],
      categoryId: number,
    ) {
      const categoryIdNewIndex = reorderedItems.findIndex(
        (item: SnapshotOrInstance<typeof ResourceCategoryModel>) =>
          item.id === categoryId,
      );

      switch (categoryIdNewIndex) {
        case 0:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.TOP_OF_PAGE,
          };
        case self.items.length - 1:
          return {
            afterId: undefined,
            moveType: REORDER_MOVE_TYPES.BOTTOM_OF_PAGE,
          };
        default:
          return {
            afterId: reorderedItems[categoryIdNewIndex - 1].id,
            moveType: REORDER_MOVE_TYPES.NORMAL,
          };
      }
    },

    updateCategory(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMoreCategories: flow(function*() {
      self.isCategoryLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(`/quick-resource/category/list`, {
            size: DEFAULT_PAGE_SIZE,
            page: self.page,
            ...self.filters,
          });

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryLoading = false;
      }
    }),

    fetchCategoryDetails: flow(function*(id: number) {
      self.isCategoryLoading = true;

      try {
        const categoryDetails = yield api.get(`/quick-resource/category/${id}`);

        applySnapshot(self, {
          ...self,
          categoryDetails,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryLoading = false;
      }
    }),

    createCategory: flow(function*(payload: any) {
      self.isCategoryManaging = true;

      try {
        return yield api.post(`/quick-resource/category`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    editCategoryInfo: flow(function*(payload: any) {
      self.isCategoryManaging = true;

      try {
        return yield api.put(`/quick-resource/category/${payload.id}`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    reorderCategories: flow(function*({
      startIndex,
      endIndex,
    }: {
      startIndex: number;
      endIndex: number;
    }) {
      self.isCategoryReordering = true;
      const { items } = getSnapshot(self);

      try {
        const categoryId = items[startIndex].id;
        const reorderedItems = reorder(items, startIndex, endIndex);
        applySnapshot(self, { ...self, items: reorderedItems });

        const reorderInfo = self.getReorderInfo(reorderedItems, categoryId);

        yield api.post(`/quick-resource/category/move`, {
          id: categoryId,
          ...reorderInfo,
        });
      } catch (error) {
        console.log("error :>> ", error);
        applySnapshot(self, { ...self, items });
        throw error;
      } finally {
        self.isCategoryReordering = false;
      }
    }),

    archiveCategory: flow(function*(id: number) {
      self.isCategoryManaging = true;

      try {
        return yield api.post(`/quick-resource/category/${id}/archive`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryManaging = false;
      }
    }),

    updatePublishStatus: flow(function*(id: number, status: string) {
      self.isCategoryPublishing = true;

      try {
        const newStatus = getNextPublishStatus(status);
        if (newStatus) {
          const { success } = yield api.post(
            `/quick-resource/category/${id}/status`,
            {
              status: newStatus,
            },
          );
          if (success) {
            self.updateCategory(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryPublishing = false;
      }
    }),

    copyCategory: flow(function*(id: number) {
      self.isCategoryCopying = true;

      try {
        yield api.post(`/quick-resource/category/${id}/copy`, {});
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isCategoryCopying = false;
      }
    }),

    resetCategoryDetails() {
      applySnapshot(self, { ...self, categoryDetails: undefined });
    },
  }));

export const InAppResourcesArchivedStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`/quick-resource/category/archived/list`, params);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: ResourceCategoryModel,
  searchBy: "search",
})
  .props({
    isManaging: types.optional(types.boolean, false),
    isMoreLoading: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get options() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, imageUrl, publishStatus }) => ({
        label: title,
        value: {
          id,
          imageUrl,
          title,
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    fetchMore: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          self.applyPage(self.page + 1);

          const { items } = yield api.get(
            `/quick-resource/category/archived/list`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
            },
          );

          applySnapshot(self, {
            ...self,
            items: [...self.items, ...items],
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    restore: flow(function*(payload: any) {
      self.isManaging = true;

      try {
        return yield api.post(`/quick-resource/category/restore`, payload);
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isManaging = false;
      }
    }),
  }))
  .create();

export default InAppResourcesStore.create();
